import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Header,
  SpaceBetween,
  Button,
  Alert,
  BreadcrumbGroup,
  Form,
  FormField,
  Input,
  Textarea,
  Select,
  StatusIndicator,
  ColumnLayout,
  Icon,
} from '@cloudscape-design/components';
import axios from 'axios';
import AppLayoutWrapper from './AppLayoutWrapper';
import { useAuth } from '../AuthContext';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';

export default function TelephonyAdd() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isEnabled } = useFeatureFlags();
  const telephonyEnabled = isEnabled('telephony');
  const [provider, setProvider] = useState({ label: 'Twilio', value: 'twilio' });
  const [name, setName] = useState('');
  const [credentialsJson, setCredentialsJson] = useState('{}');
  // SIP Trunk fields
  const [trunkName, setTrunkName] = useState('');
  const [phoneNumbers, setPhoneNumbers] = useState('');
  const [allowedAddresses, setAllowedAddresses] = useState('');
  const [allowedIps, setAllowedIps] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [quota, setQuota] = useState({ total: 0, used: 0, remaining: 0, loading: true });

  const telephonyQuotaAvailable = quota.remaining > 0;
  const canCreateTelephony = telephonyEnabled && telephonyQuotaAvailable;

  // Assignment mode: directly to an agent or reserved on a pack
  const [assignmentMode, setAssignmentMode] = useState({ label: 'Reserve on a pack', value: 'pack' });
  const [agents, setAgents] = useState([]);
  const [packs, setPacks] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedPack, setSelectedPack] = useState(null);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [loadingPacks, setLoadingPacks] = useState(false);

  const accessToken = user?.access_token || user?.token;

  useEffect(() => {
    const fetchAgents = async () => {
      if (!accessToken) return;
      setLoadingAgents(true);
      try {
        const resp = await axios.get('/api/agents/for-assignment/', {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        if (resp.data?.status === 'success' && Array.isArray(resp.data.data)) {
          setAgents(resp.data.data.map(a => ({ label: a.name || a.id, value: a.id })));
        }
      } catch (err) {
        console.error('Error fetching agents:', err);
      } finally {
        setLoadingAgents(false);
      }
    };

    const fetchPacks = async () => {
      if (!accessToken) return;
      setLoadingPacks(true);
      try {
        const resp = await axios.get('/api/company-packs/', {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        if (resp.data?.status === 'success' && Array.isArray(resp.data.data)) {
          setPacks(resp.data.data
            .filter(p => p.quotas?.telephony_numbers?.remaining > 0)
            .map(p => {
              const telTotal = p.quotas?.telephony_numbers?.total || 0;
              const telUsed = p.quotas?.telephony_numbers?.used || 0;
              const statusLabel = p.status === 'inventory' ? 'In stock' : (p.has_agent ? 'Assigned' : 'Active');
              return {
                label: `${p.name} (${telUsed}/${telTotal} Phone Numbers) — ${statusLabel}`,
                value: p.id
              };
            }));
        }
      } catch (err) {
        console.error('Error fetching packs:', err);
      } finally {
        setLoadingPacks(false);
      }
    };

    const fetchQuota = async () => {
      if (!accessToken) return;
      try {
        const resp = await axios.get('/api/billing/quota/', {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        const resources = resp.data?.data?.resources || {};
        const tel = resources.twilio_numbers || { total: 0, used: 0, remaining: 0 };
        setQuota({ total: tel.total, used: tel.used, remaining: tel.remaining, loading: false });
      } catch (err) {
        console.error('Error fetching quota:', err);
        setQuota(prev => ({ ...prev, loading: false }));
      }
    };

    fetchAgents();
    fetchPacks();
    fetchQuota();
  }, [accessToken]);

  const providerOptions = [
    { label: 'Twilio', value: 'twilio' },
    { label: 'SIP Trunk', value: 'sip_trunk' },
  ];

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError('SIP configuration name is required');
      return;
    }
    if (assignmentMode.value === 'agent' && !selectedAgent) {
      setError('Please select an agent');
      return;
    }
    if (assignmentMode.value === 'pack' && !selectedPack) {
      setError('Please select a pack');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      let payload = {
        provider: provider.value,
        name: name.trim(),
      };
      if (provider.value === 'twilio') {
        let credentials;
        try {
          credentials = JSON.parse(credentialsJson.trim() || '{}');
        } catch {
          setError('Credentials JSON is invalid');
          setLoading(false);
          return;
        }
        payload.credentials = credentials;
      } else if (provider.value === 'sip_trunk') {
        if (!trunkName.trim()) {
          setError('Trunk name is required for SIP Trunk');
          setLoading(false);
          return;
        }
        if (!phoneNumbers.trim()) {
          setError('Phone numbers are required for SIP Trunk');
          setLoading(false);
          return;
        }
        payload.credentials = {
          trunk_name: trunkName.trim(),
          phone_numbers: phoneNumbers.split(',').map(s => s.trim()).filter(Boolean),
          allowed_addresses: allowedAddresses.split(',').map(s => s.trim()).filter(Boolean),
          allowed_ips: allowedIps.split(',').map(s => s.trim()).filter(Boolean),
        };
      }
      payload.phone_numbers = payload.credentials?.phone_numbers || [];
      if (assignmentMode.value === 'agent') {
        payload.agent_id = selectedAgent.value;
      } else {
        payload.company_pack_id = selectedPack.value;
      }
      const resp = await axios.post('/api/sip/create/', payload, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (resp.data?.status === 'confirmation_required') {
        payload.confirm_resource_activation = true;
        await axios.post('/api/sip/create/', payload, {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
      }
      setSuccess(true);
      setTimeout(() => navigate('/telephony'), 1200);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to create SIP configuration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppLayoutWrapper
      breadcrumbs={
        <BreadcrumbGroup
          items={[
            { text: 'Home', href: '/dashboard' },
            { text: 'Telephony', href: '/telephony' },
            { text: 'Add Telephony', href: '/telephony/new' },
          ]}
        />
      }
      header={{
        title: 'Add Telephony',
        description: 'Create a new SIP/telephony configuration for your workspace.',
      }}
      headerBackgroundStyle="linear-gradient(to bottom, rgba(9, 29, 43, 1) 2%, rgba(0, 108, 224, 1) 8%, rgba(255, 255, 255, 1) 82%)"
      helpPanelHeader="Telephony"
      helpPanelContent={
        <div>
          <p>Create workspace-level SIP configurations that can be assigned to agents.</p>
          <ul>
            <li>Provider selection</li>
            <li>Credentials configuration</li>
            <li>Agent assignment via Telephony page</li>
          </ul>
        </div>
      }
    >
      <SpaceBetween size="l">
        {success && (
          <Alert type="success" header="SIP configuration created">
            Redirecting to telephony list...
          </Alert>
        )}
        {!telephonyEnabled && (
          <Alert type="warning" header="Feature disabled">
            Telephony is currently disabled by the administrator.
          </Alert>
        )}
        {!quota.loading && !telephonyQuotaAvailable && telephonyEnabled && (
          <Alert type="warning" header="Quota exceeded">
            Telephony number quota exceeded ({quota.used}/{quota.total}). Purchase a pack with more telephony numbers.
          </Alert>
        )}
        {error && <Alert type="error" header="Error">{error}</Alert>}

        <Container header={<Header variant="h2"><Icon name="phone" variant="normal" /> Telephony configuration</Header>}>
          <Form
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => navigate('/telephony')}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleSubmit} loading={loading} disabled={!canCreateTelephony}>
                  Create SIP Config
                </Button>
              </SpaceBetween>
            }
          >
            <SpaceBetween size="l">
              <ColumnLayout columns={{ default: 1, s: 2 }}>
                <FormField label="Provider" constraintText="Required">
                  <Select
                    selectedOption={provider}
                    onChange={({ detail }) => setProvider(detail.selectedOption)}
                    options={providerOptions}
                  />
                </FormField>
                <FormField label="Configuration Name" constraintText="Required">
                  <Input
                    value={name}
                    onChange={({ detail }) => setName(detail.value)}
                    placeholder="e.g., Main SIP Trunk"
                  />
                </FormField>
              </ColumnLayout>

              <Container header={<Header variant="h3">Assignment</Header>}>
                <SpaceBetween size="l">
                  <FormField label="Assign To" description="Associate this telephony resource with an existing agent or reserve it on a pack.">
                    <Select
                      selectedOption={assignmentMode}
                      onChange={({ detail }) => {
                        setAssignmentMode(detail.selectedOption);
                        setSelectedAgent(null);
                        setSelectedPack(null);
                      }}
                      options={[
                        { label: 'Reserve on a pack', value: 'pack' },
                        { label: 'Existing agent', value: 'agent' },
                      ]}
                    />
                  </FormField>
                  {assignmentMode.value === 'agent' && (
                    <FormField label="Select Agent" description="Choose an agent that will use this telephony resource.">
                      <Select
                        selectedOption={selectedAgent}
                        onChange={({ detail }) => setSelectedAgent(detail.selectedOption)}
                        options={agents}
                        loading={loadingAgents}
                        placeholder="Select an agent"
                      />
                    </FormField>
                  )}
                  {assignmentMode.value === 'pack' && (
                    <FormField label="Select Pack" description="Reserve this telephony resource on a pack. It can be assigned to an agent later, but must match the same pack.">
                      <Select
                        selectedOption={selectedPack}
                        onChange={({ detail }) => setSelectedPack(detail.selectedOption)}
                        options={packs}
                        loading={loadingPacks}
                        placeholder="Select a pack"
                      />
                    </FormField>
                  )}
                </SpaceBetween>
              </Container>

              {provider.value === 'twilio' && (
                <Container header={<Header variant="h3">Twilio credentials</Header>}>
                  <FormField label="Credentials (JSON)" description="Provider-specific credentials">
                    <Textarea
                      value={credentialsJson}
                      onChange={({ detail }) => setCredentialsJson(detail.value)}
                      placeholder='{"api_key": "your-key", "api_secret": "your-secret", "account_sid": "ACxxx"}'
                    />
                  </FormField>
                </Container>
              )}
              {provider.value === 'sip_trunk' && (
                <Container header={<Header variant="h3">SIP trunk details</Header>}>
                  <SpaceBetween size="l">
                    <ColumnLayout columns={{ default: 1, s: 2 }}>
                      <FormField label="Trunk Name" constraintText="Required">
                        <Input value={trunkName} onChange={({ detail }) => setTrunkName(detail.value)} placeholder="Inbound Medical Trunk" />
                      </FormField>
                      <FormField label="Phone Numbers" constraintText="Required (comma-separated)">
                        <Input value={phoneNumbers} onChange={({ detail }) => setPhoneNumbers(detail.value)} placeholder="+33123456789, +33698765432" />
                      </FormField>
                    </ColumnLayout>
                    <ColumnLayout columns={{ default: 1, s: 2 }}>
                      <FormField label="Allowed Addresses (comma-separated)" description="Optional">
                        <Input value={allowedAddresses} onChange={({ detail }) => setAllowedAddresses(detail.value)} placeholder="sip:provider1.com" />
                      </FormField>
                      <FormField label="Allowed IPs (comma-separated)" description="Optional">
                        <Input value={allowedIps} onChange={({ detail }) => setAllowedIps(detail.value)} placeholder="203.0.113.0/24" />
                      </FormField>
                    </ColumnLayout>
                  </SpaceBetween>
                </Container>
              )}
              {loading && <StatusIndicator type="loading">Creating SIP configuration...</StatusIndicator>}
            </SpaceBetween>
          </Form>
        </Container>
      </SpaceBetween>
    </AppLayoutWrapper>
  );
}
